import { del, get, post, put, type ReqConfig } from '../request';
import { toPage, type Page, type PageQuery } from './types';

// ---------- 登录 ----------

export interface LoginResp {
  token: string;
  user?: any;
  [k: string]: any;
}

/** POST /login 阶段一（密码正确但需要双因子时返回 mfa_required/step_token） */
export function login(
  data: { username: string; password: string },
  cfg?: ReqConfig
): Promise<LoginResp> {
  return post('/login', data, cfg);
}

/** POST /login/2fa 阶段二：{ step_token, code, method?, use_recovery? } */
export function login2fa(
  data: { step_token: string; code: string; method?: string; use_recovery?: boolean },
  cfg?: ReqConfig
): Promise<LoginResp> {
  return post('/login/2fa', data, cfg);
}

/** POST /login/2fa/sms 阶段二短信通道发码：{ step_token } → { masked_phone } */
export async function login2faSendSMS(stepToken: string): Promise<{ masked_phone: string }> {
  return post('/login/2fa/sms', { step_token: stepToken }, { silent: true });
}

// ---------- 2FA 绑定/管理（/mfa/*，docs/login-2fa.md） ----------

export interface MFASetupResp {
  otpauth_uri: string;
  secret_b32: string;
  recovery_codes?: string[];
  already_bound?: boolean;
}

/** GET /mfa/setup —— 绑定信息（未启用生成密钥+恢复码；已启用返回当前密钥） */
export async function mfaSetup(cfg?: ReqConfig): Promise<MFASetupResp> {
  const data = await get('/mfa/setup', undefined, cfg);
  return (data && data.data) || data;
}

/** POST /mfa/verify { code } —— 首绑校验启用 */
export function mfaVerify(code: string, cfg?: ReqConfig) {
  return post('/mfa/verify', { code }, cfg);
}

/** POST /users/:id/disable-2fa —— admin 故障救援（step-up） */
export function disableUser2FA(id: number, v: { admin_password: string; admin_code: string }) {
  return post(`/users/${id}/disable-2fa`, v);
}

/** POST /users/:id/unlock —— admin 轻量解锁（step-up，仅清锁定，不动 2FA/密码） */
export function unlockUser(id: number, v: { admin_password: string; admin_code: string }) {
  return post(`/users/${id}/unlock`, v);
}

/** POST /users/change-password —— 当前用户改密 { old_password, new_password } */
export function changePassword(v: { old_password: string; new_password: string }) {
  return post('/users/change-password', v);
}

// ---------- 用户 ----------

export interface SysUser {
  id: number;
  username: string;
  name?: string;
  /** 主角色：派生字段，取角色集合里优先级最高的内置角色（管理员 > 主管 > 坐席）；授权真源是 roles */
  role?: string;
  /** 角色集合（sys_user_role 绑定）：多角色权限取并集，附加角色如坐席兼任省级采编 */
  roles?: string[];
  /** 账号状态 active=启用 / disabled=禁用（锁定态由 locked_until 判断） */
  status?: string;
  /** 锁定截止（连续失败 5 次锁 10 分钟，非空且未到期=锁定中） */
  locked_until?: string | null;
  /** 技能组（主管档的数据范围，可空） */
  skill_group_id?: number | null;
  /** 在线接待量自定：nil=跟随全局 chat.max_sessions_per_agent */
  chat_max_sessions?: number | null;
  phone?: string;
  email?: string;
  /** 2FA：TOTP/短信验证码是否已启用（docs/login-2fa.md） */
  totp_enabled?: boolean;
  sms_enabled?: boolean;
  last_login_at?: string | null;
  last_login_ip?: string;
  created_at?: string;
  [k: string]: any;
}

export interface UserQuery extends PageQuery {
  username?: string;
  role?: string;
  status?: string;
  /** 按所属组织筛选：逗号分隔的多值（组织树筛选取「选中节点 + 全部下级」，见 utils/orgTree.ts orgSubtreeIds） */
  org_id?: string;
}

export async function listUsers(q: UserQuery): Promise<Page<SysUser>> {
  return toPage<SysUser>(await get('/users', q));
}

/** 运营处置只读选人选项（v5.2，MONITOR 面 admin+supervisor）：id/name/username/role，无 PII */
export interface UserOption {
  id: number;
  username: string;
  name: string;
  role: string;
  skill_group_id?: number | null;
}
export async function listUserOptions(): Promise<UserOption[]> {
  const res = await get('/users/options');
  return Array.isArray(res) ? (res as UserOption[]) : [];
}

export function createUser(v: {
  username: string;
  name: string;
  /** 角色集合（至少一个）：写绑定表，权限取并集；主角色由后端派生 */
  roles: string[];
  password: string;
  skill_group_id?: number;
  chat_max_sessions?: number | null;
  phone?: string;
  email?: string;
}) {
  return post('/users', v);
}

export function updateUser(
  id: number,
  v: {
    name?: string;
    /** 出现即按「该用户的角色集合就是它」全量替换绑定；不传则完全不动角色 */
    roles?: string[];
    status?: string;
    skill_group_id?: number;
    chat_max_sessions?: number | null;
    phone?: string;
    email?: string;
  }
) {
  return put(`/users/${id}`, v);
}

export function deleteUser(id: number) {
  return del(`/users/${id}`);
}

/** POST /users/:id/reset-password —— body { password } */
export function resetUserPassword(id: number, password: string) {
  return post(`/users/${id}/reset-password`, { password });
}

// ---------- 审计日志（admin） ----------

export interface AuditLog {
  id: number;
  user_id?: number;
  username?: string;
  /** login/login_failed/create/update/delete/export/reset_password/claim/transfer/sync */
  action: string;
  /** 操作模块（用户管理/呼叫路由/AI中台/知识库/在线客服/外呼/系统设置…） */
  module?: string;
  /** 功能（被操作对象类型） */
  function?: string;
  /** 操作结果 success|fail */
  result?: string;
  target?: string;
  detail?: string;
  ip?: string;
  created_at?: string;
  [k: string]: any;
}

export interface AuditLogQuery extends PageQuery {
  action?: string;
  result?: string;
  username?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** GET /audit-logs */
export async function listAuditLogs(q: AuditLogQuery): Promise<Page<AuditLog>> {
  return toPage<AuditLog>(await get('/audit-logs', q));
}

// ---------- SIP 网关 ----------

export interface Gateway {
  id: number;
  name: string;
  host: string;
  port: number;
  register?: boolean;
  username?: string;
  password?: string; // 列表脱敏为 "******"
  codec?: string; // 逗号拼接字符串
  enabled?: boolean;
  [k: string]: any;
}

/** 表单态：codecs 为多选数组 */
export interface GatewayForm {
  name: string;
  host: string;
  port: number;
  register: boolean;
  username?: string;
  password?: string; // 编辑时留空 = 不修改
  codecs: string[];
  enabled: boolean;
}

/** GET /gateways */
export async function listGateways(q?: PageQuery): Promise<Page<Gateway>> {
  return toPage<Gateway>(await get('/gateways', { page: 1, pageSize: 100, ...(q || {}) }));
}

/** 读取：codec 逗号字符串 → 数组 */
export function gatewayToForm(g: Gateway): GatewayForm {
  return {
    name: g.name || '',
    host: g.host || '',
    port: g.port ?? 5060,
    register: !!g.register,
    username: g.username || '',
    password: '', // 脱敏掩码不回填；留空 = 不修改
    codecs: g.codec ? String(g.codec).split(',').map((s) => s.trim()).filter(Boolean) : ['PCMU', 'PCMA'],
    enabled: !!g.enabled,
  };
}

/** 提交：codecs 数组 → codec 逗号字符串；password 为空则不携带 */
function toGatewayPayload(f: Partial<GatewayForm>): Record<string, any> {
  const p: Record<string, any> = {};
  if (f.name !== undefined) p.name = f.name;
  if (f.host !== undefined) p.host = f.host;
  if (f.port !== undefined) p.port = f.port;
  if (f.register !== undefined) p.register = f.register;
  if (f.username !== undefined) p.username = f.username;
  if (f.password) p.password = f.password; // 留空 = 不修改
  if (f.codecs !== undefined) p.codec = f.codecs.join(',');
  if (f.enabled !== undefined) p.enabled = f.enabled;
  return p;
}

export function createGateway(f: GatewayForm) {
  return post('/gateways', toGatewayPayload(f));
}

export function updateGateway(id: number, f: Partial<GatewayForm>) {
  return put(`/gateways/${id}`, toGatewayPayload(f));
}

/** 启停开关：只提交 { enabled }（后端为白名单部分更新） */
export function setGatewayEnabled(id: number, enabled: boolean) {
  return put(`/gateways/${id}`, { enabled });
}

export function deleteGateway(id: number) {
  return del(`/gateways/${id}`);
}
